import React from 'react'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import {
  numberWithComma,
  formatDate,
  formatAddressNoTitle,
  formatShippingTypes,
  formatPaymentChannel,
  getDefaultValueConfig,
  replaceStr,
} from 'helpers/format'
import { Data, Product } from 'Store/orders/types'
import { Col, Row } from 'antd'
import {
  menuDeliverySignType,
  NOT_SHOW_PRICE_TEXT,
  PaymentType,
  StockMovementChannel,
  VAT_TYPE,
  VAT_TYPE_TEXT,
} from 'helpers/constanst'
import { PdfPrintFormat, PdfPrintLiteFormat } from 'Components/form'
import ProductBarcode from 'Components/utilities/ProductBarcode'
import { themeConfig } from 'config'
import { ConfigData } from 'Store/pos/types'

type Props = {
  detail?: Data
  isLite?: boolean
  title: string
  configData?: ConfigData
}

const InvoicePDF: React.FunctionComponent<Props> = props => {
  const { detail, isLite = false, title, configData } = props
  const showPrice = !title.includes(NOT_SHOW_PRICE_TEXT)

  const products = detail?.products[0]?.products || []
  const shippingType = detail?.products[0]?.products[0]?.shipping_detail?.delivery_by || ''
  const isCod = detail?.payment_detail?.payment_type === PaymentType.COD
  const order = detail?.products[0] || undefined
  const orderNo = detail?.order_no
  const isPos = detail?.channel === StockMovementChannel.POS
  const config = configData && configData?.config_value && JSON.parse(configData?.config_value || '')

  const renderInfo = (val: any) => {
    return <RowTile>{val}</RowTile>
  }

  const getTotalBeforeDiscount = () => {
    let total = 0
    products.forEach((item: Product) => {
      total += item.price_before_discount
    })
    return total
  }

  const getTotalDiscount = () => {
    let total = 0
    products.forEach((item: Product) => {
      total += item.total_discount
    })
    return total
  }

  const getTotalProductPrice = () => {
    let total = 0
    products.forEach((item: Product) => {
      total += item.total_price
    })
    return total
  }

  const renderInfoItem = (title: any, value: any) => {
    return (
      <Row justify="space-between">
        <Col>
          <InfoTitle>{title}</InfoTitle>
        </Col>
        <Col offset={1}>
          <InfoValue>{value || '-'}</InfoValue>
        </Col>
      </Row>
    )
  }

  const renderSenderInfo = () => {
    return (
      <Row>
        <Col span={24}>
          <BoxTitle>{order?.merchant_name}</BoxTitle>
          <InfoValue>{order?.merchant_address}</InfoValue>
          <InfoValue>เบอร์โทรศัพท์ {order?.merchant_tel}</InfoValue>
        </Col>
      </Row>
    )
  }

  const renderRecieverInfo = () => {
    const receiver = detail?.shipping_address

    const info = (title: string, value: any) => (
      <Row justify="space-between">
        <Col span={6}>
          <InfoValue>
            <Bold>{title}</Bold>
          </InfoValue>
        </Col>
        <Col span={18}>
          <InfoValue>{value}</InfoValue>
        </Col>
      </Row>
    )
    return (
      <div>
        {info('เลขที่สั่งซื้อ', orderNo)}
        {info('ลูกค้า', `คุณ${receiver?.receiver_name}`)}
        {info(
          'ที่อยู่จัดส่ง',
          formatAddressNoTitle(
            receiver?.address || '',
            receiver?.sub_district || '',
            receiver?.district || '',
            receiver?.province || '',
            receiver?.zipcode || '',
          ),
        )}
        {info('โทรศัพท์', receiver?.tel)}
        {info('ขนส่งโดย', formatShippingTypes(shippingType))}
        {info('การชำระเงิน', formatPaymentChannel(detail?.payment_detail?.payment_type || ''))}
      </div>
    )
  }

  const renderProductName = (item: any, record: any) => {
    const isSameTitle = item.title == record.product_option?.title
    return (
      <>
        <RowTile>{item.title}</RowTile>
        {!isSameTitle && <ProductDesc>{record.product_option?.title}</ProductDesc>}
      </>
    )
  }

  const totalBeforeDiscount = getTotalBeforeDiscount()
  const totalDiscount = getTotalDiscount()
  const tailDiscount = detail?.payment_detail?.tail_discount_amount || 0
  const totalProductPrice = getTotalProductPrice() - tailDiscount
  const shippingPrice = detail?.products[0]?.shipping_price || 0
  const paymentFee = detail?.products[0]?.payment_fee || 0
  const total = detail?.payment_detail?.total
  const couponDiscount = detail?.payment_detail?.coupon_discount_amount || 0
  const isBigBangPromo = (detail?.payment_detail?.bigbang_promotion_discount || 0) > 0
  const isExcludeVat = detail?.payment_detail?.vat_type === VAT_TYPE.EX
  const isTax = detail?.payment_detail.is_tax
  const vatType = detail?.payment_detail?.vat_type
  const vatAmount = detail?.payment_detail?.vat_amount || 0

  const summaryFooter = (
    <Row justify="end">
      <Col span={12}>
        {totalDiscount > 0 &&
          renderInfoItem('รวมราคาสินค้าก่อนหักส่วนลด', `${numberWithComma(totalBeforeDiscount, true)} บาท`)}
        {totalDiscount > 0 &&
          renderInfoItem('หัก ส่วนลดสินค้า', `${numberWithComma(totalDiscount, true)} บาท`)}
        {renderInfoItem('รวมราคาสินค้า', `${numberWithComma(getTotalProductPrice(), true)} บาท`)}
        {tailDiscount > 0 &&
          renderInfoItem('หัก ส่วนลดท้ายบิล ', `${numberWithComma(tailDiscount, true)} บาท`)}
        {tailDiscount > 0 &&
          renderInfoItem('ราคารวมทั้งสิ้น ', `${numberWithComma(totalProductPrice, true)} บาท`)}
        {isExcludeVat &&
          isTax &&
          renderInfoItem(
            `ภาษีมูลค่าเพิ่ม 7% (${vatType === VAT_TYPE.EX ? VAT_TYPE_TEXT.EX : VAT_TYPE_TEXT.IN})`,
            `${numberWithComma(vatAmount, true)} บาท`,
          )}
        {renderInfoItem('ค่าจัดส่ง', `${numberWithComma(shippingPrice, true)} บาท`)}
        {isCod && renderInfoItem('ค่าธุรกรรมทางการเงิน', `${numberWithComma(paymentFee, true)} บาท`)}
        {couponDiscount > 0 && <Remark>คำสั่งซื้อนี้มีการใช้คูปองส่วนลดร่วมด้วย</Remark>}
        {isBigBangPromo && (
          <Row>
            <Col span={20}>
              <Remark>
                คำสั่งซื้อนี้มีโปรโมชันส่งเสริมการขายประเภทส่วนลดพิเศษจาก {themeConfig.title.appName} ร่วมด้วย
              </Remark>
            </Col>
          </Row>
        )}
        {renderInfoItem('จำนวนเงินรวมทั้งสิ้น', <TotalPrice>{numberWithComma(total, true)} บาท</TotalPrice>)}
      </Col>
    </Row>
  )

  const tableHeader = (
    <thead>
      <th>ลำดับ</th>
      {!isLite && (
        <th>
          รหัสสินค้า <br />
          Barcode
        </th>
      )}

      <th className="left" style={{ width: '30%' }}>
        รายการ
      </th>
      <th>ยี่ห้อ</th>
      <th>จำนวน</th>
      <th>หน่วย</th>
      {showPrice && isPos && <th className="right">ราคา/หน่วย</th>}
      {showPrice && isPos && <th className="right">ส่วนลด/หน่วย</th>}
      {showPrice && isPos && <th className="right">ราคารวม</th>}
      {showPrice && !isPos && (
        <th>
          ราคารวม <br />
          (ก่อนหักส่วนลด)
        </th>
      )}
      {showPrice && !isPos && <th>ส่วนลด</th>}
      {showPrice && !isPos && (
        <th>
          ราคารวม <br />
          (หลังหักส่วนลด)
        </th>
      )}
    </thead>
  )

  const tableBody = (
    <tbody>
      {products.map((item: Product, index: number) => {
        const barcode = item?.product_option?.barcode || ''
        return (
          <tr key={index}>
            <td>{renderInfo(index + 1)}</td>
            {!isLite && (
              <td>
                {renderInfo(
                  <>
                    {item?.product?.code || '-'} <br />
                    {barcode && <ProductBarcode barcode={barcode} />}
                  </>,
                )}
              </td>
            )}

            <td className="left">{renderProductName(item?.product, item)}</td>
            <td>{renderInfo(item?.product?.manufacturer?.name || '-')}</td>
            <td>{renderInfo(numberWithComma(item?.quantity))}</td>
            <td>{renderInfo(item?.product_uom || '-')}</td>
            {showPrice && isPos && (
              <td className="right">
                {renderInfo(numberWithComma(item?.price_before_discount_per_unit || 0, true))}
              </td>
            )}
            {showPrice && isPos && (
              <td className="right">{renderInfo(numberWithComma(item?.discount || 0, true))}</td>
            )}
            {showPrice && !isPos && (
              <td className="right">{renderInfo(numberWithComma(item.price_before_discount, true))}</td>
            )}
            {showPrice && !isPos && (
              <td className="right">{renderInfo(numberWithComma(item.total_discount, true))}</td>
            )}
            {showPrice && <td className="right">{renderInfo(numberWithComma(item.total_price, true))}</td>}
          </tr>
        )
      })}
    </tbody>
  )

  const remarkFooter = () => {
    const remark = config?.remark || ''
    return remark ? (
      <Row justify="start" gutter={[8, 8]} align="bottom">
        <Col span={24}>
          <div style={{ textAlign: 'left' }}>{remark}</div>
        </Col>
      </Row>
    ) : null
  }
  const replaceTitle = replaceStr(title, NOT_SHOW_PRICE_TEXT, '')

  return isLite ? (
    <PdfPrintLiteFormat
      companyInfo={renderSenderInfo()}
      title={replaceTitle}
      leftInfo={renderRecieverInfo()}
      rightInfo={<InfoValue>วันที่สั่งซื้อ {formatDate(detail?.created_at || '')}</InfoValue>}
      footerSpan={9}
      summaryFooter={showPrice ? summaryFooter : null}
      signPosition={config?.signature ? config?.signature : getDefaultValueConfig(menuDeliverySignType)}
      thead={tableHeader}
      tbody={tableBody}
      remarkFooter={remarkFooter()}
    />
  ) : (
    <PdfPrintFormat
      companyInfo={renderSenderInfo()}
      title={replaceTitle}
      leftInfo={renderRecieverInfo()}
      rightInfo={<InfoValue>วันที่สั่งซื้อ {formatDate(detail?.created_at || '')}</InfoValue>}
      footerSpan={9}
      summaryFooter={showPrice ? summaryFooter : null}
      signPosition={config?.signature ? config?.signature : getDefaultValueConfig(menuDeliverySignType)}
      thead={tableHeader}
      tbody={tableBody}
      remarkFooter={remarkFooter()}
    />
  )
}

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
`

const RowTile = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const ProductDesc = styled(RowTile)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const InfoTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const InfoValue = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  margin-right: 10px;
`

const Bold = styled.span`
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
`

const TotalPrice = styled(Bold)`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const Remark = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

export default InvoicePDF
